import React from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useInView } from "framer-motion";
import {
  ArrowRight,
  Bot,
  Calendar,
  Check,
  Code2,
  MessageSquare,
  Rocket,
  RotateCcw,
  Send,
  Sparkles,
  TrendingUp,
  Users,
  X,
  Zap,
  Globe,
  Smartphone,
  Search,
  CreditCard,
  Target,
} from "lucide-react";
import KaiChatPanel from "@/components/KaiChatPanel";

const container = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const CORE = [
  { icon: Globe, label: "websites" },
  { icon: Bot, label: "ai automations" },
  { icon: TrendingUp, label: "growth" },
  { icon: Calendar, label: "bookings" },
  { icon: Sparkles, label: "ai agents" },
];

const SERVICES = [
  {
    icon: Code2,
    title: "web development",
    desc: "conversion-first websites, built fast, indexed by google, wired to your crm.",
    big: true,
  },
  {
    icon: Bot,
    title: "ai chatbots",
    desc: "24/7 receptionists that answer, qualify, and book — trained on your brand voice.",
    big: true,
  },
  {
    icon: Target,
    title: "lead qualification",
    desc: "score, tag and route every message so only warm leads reach your team.",
  },
  {
    icon: Calendar,
    title: "bookings & reservations",
    desc: "instant slot picking on your site, whatsapp, or instagram.",
  },
  {
    icon: Zap,
    title: "crm & automations",
    desc: "whatsapp, instagram, email — all your channels in one automated pipeline.",
  },
];

const BEFORE = [
  "replies take hours, prospects go cold",
  "team drowns in repetitive dms",
  "no-shows and missed appointments",
  "leads scattered across 5 different apps",
  "manual follow-ups you forget to send",
];
const AFTER = [
  "instant replies, 24/7, in your voice",
  "ai handles 80% before a human touches it",
  "automatic reminders on whatsapp & sms",
  "every lead in one crm, ready to close",
  "follow-ups that run themselves for weeks",
];

const CHAT = [
  { from: "ai", text: "hi 👋 i'm kai — kyvixe's ai receptionist. how can i help today?" },
  { from: "user", text: "do you build websites for restaurants?" },
  { from: "ai", text: "yes — modern, mobile-first, with online ordering & reservations. want a quick estimate?" },
  { from: "user", text: "sure. what's included?" },
  { from: "ai", text: "website + booking + whatsapp bot. shipped in 2 weeks. want me to book a 30-min discovery call?" },
  { from: "user", text: "yes please, tomorrow morning" },
  { from: "ai", text: "booked ✓ tomorrow at 10:30 am. confirmation on its way to your inbox." },
];

const READ_MS_PER_CHAR = 22;
const MIN_READ_MS = 900;
const MAX_READ_MS = 2400;
const AI_TYPING_MS = 900;

function AiChatDemo() {
  const ref = React.useRef(null);
  const inView = useInView(ref, { amount: 0.35, once: false });

  const [visible, setVisible] = React.useState([]);
  const [typing, setTyping] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [runId, setRunId] = React.useState(0);
  const scrollRef = React.useRef(null);

  React.useEffect(() => {
    if (!inView) return;
    let cancelled = false;
    const timers = [];
    setVisible([]);
    setTyping(false);
    setDone(false);

    let cursor = 300;
    CHAT.forEach((msg, i) => {
      if (msg.from === "ai") {
        timers.push(setTimeout(() => !cancelled && setTyping(true), cursor));
        cursor += AI_TYPING_MS;
      }
      timers.push(
        setTimeout(() => {
          if (cancelled) return;
          setTyping(false);
          setVisible((v) => [...v, msg]);
        }, cursor),
      );
      const readMs = Math.min(
        MAX_READ_MS,
        Math.max(MIN_READ_MS, msg.text.length * READ_MS_PER_CHAR),
      );
      cursor += readMs;
      if (i === CHAT.length - 1) {
        timers.push(setTimeout(() => !cancelled && setDone(true), cursor));
      }
    });

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [inView, runId]);

  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [visible, typing]);

  const replay = () => setRunId((n) => n + 1);

  return (
    <div className="relative" ref={ref}>
      <div className="absolute -inset-4 bg-blue-500/10 blur-3xl rounded-3xl" />
      <div className="relative kyv-card p-6 backdrop-blur-xl" data-testid="chat-mockup">
        <div className="flex items-center justify-between pb-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400">
              <Bot size={18} />
            </div>
            <div>
              <p className="font-display lowercase font-bold text-lg leading-none">kai</p>
              <p className="text-xs text-blue-400 flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                online
              </p>
            </div>
          </div>
          {done && (
            <button
              onClick={replay}
              className="text-xs text-gray-400 hover:text-blue-300 inline-flex items-center gap-1.5"
              data-testid="chat-replay-btn"
            >
              <RotateCcw size={12} /> replay
            </button>
          )}
        </div>

        <div
          ref={scrollRef}
          className="space-y-3 py-5 min-h-[260px] max-h-[300px] overflow-hidden"
          data-testid="chat-messages"
        >
          <AnimatePresence initial={false}>
            {visible.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.25 }}
                className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`px-4 py-2.5 rounded-2xl max-w-[80%] text-sm ${
                    m.from === "user"
                      ? "bg-white/8 text-gray-100 rounded-br-sm"
                      : "bg-blue-600 text-white rounded-bl-sm"
                  }`}
                >
                  {m.text}
                </div>
              </motion.div>
            ))}
            {typing && (
              <motion.div
                key="typing"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex justify-start"
              >
                <div className="px-4 py-3 rounded-2xl rounded-bl-sm bg-blue-600/70 flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-white/90 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-white/90 animate-bounce" style={{ animationDelay: "120ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-white/90 animate-bounce" style={{ animationDelay: "240ms" }} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/10">
          {["book appointment", "get pricing", "talk to ai", "contact team"].map((b, i) => (
            <motion.button
              key={i}
              initial={{ opacity: 0.5, y: 4 }}
              animate={done ? { opacity: 1, y: 0 } : { opacity: 0.5, y: 4 }}
              transition={{ duration: 0.35, delay: done ? i * 0.05 : 0 }}
              className="text-xs px-3 py-2 rounded-full bg-white/5 border border-white/10 text-gray-200 hover:border-blue-500/50 hover:text-blue-300 transition"
              data-testid={`chat-cta-${i}`}
            >
              {b}
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
}

const WEB_FEATURES = [
  { icon: Smartphone, label: "modern & responsive" },
  { icon: Search, label: "seo optimized" },
  { icon: Zap, label: "lightning fast" },
  { icon: Bot, label: "ai integrated" },
  { icon: CreditCard, label: "bookings & payments" },
  { icon: Rocket, label: "built to convert" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-hero-glow" data-testid="hero-section">
        <div className="absolute inset-0 grid-lines pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-36">
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="max-w-4xl mx-auto text-center flex flex-col items-center"
          >
            <motion.h1
              variants={item}
              className="font-display lowercase font-bold tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[1.02]"
              data-testid="hero-heading"
            >
              build. <span className="text-blue-500">automate.</span> scale.
            </motion.h1>
            <motion.p
              variants={item}
              className="mt-6 text-lg sm:text-xl text-gray-400 max-w-2xl leading-relaxed"
            >
              ai solutions for modern businesses — websites, chatbots, whatsapp
              & instagram automations, and booking systems that never sleep.
            </motion.p>
            <motion.div variants={item} className="mt-10 flex flex-wrap gap-4 justify-center">
              <Link
                to="/contact#book"
                className="kyv-btn-primary inline-flex items-center gap-2"
                data-testid="hero-book-a-call-btn"
              >
                book a call <ArrowRight size={16} />
              </Link>
              <Link to="/services" className="kyv-btn-ghost inline-flex items-center gap-2" data-testid="hero-services-btn">
                get started
              </Link>
            </motion.div>

            <motion.div
              variants={item}
              className="mt-16 grid grid-cols-2 sm:grid-cols-5 gap-3 max-w-3xl w-full"
              data-testid="hero-core-row"
            >
              {CORE.map((c, i) => (
                <div
                  key={i}
                  className="kyv-card px-4 py-4 flex flex-col items-center text-center gap-2"
                >
                  <div className="p-2 rounded-full bg-blue-500/10 text-blue-400">
                    <c.icon size={18} />
                  </div>
                  <span className="text-xs text-gray-300 lowercase">{c.label}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="bg-section-glow py-24 lg:py-32" data-testid="what-we-do-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <h2 className="font-display lowercase font-bold text-4xl sm:text-5xl tracking-tighter">
              five services. <span className="text-blue-500">one revenue engine.</span>
            </h2>
            <p className="mt-4 text-gray-400">
              built to work together — every service plugs into the next so your
              site, chat, and crm behave like one system.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
            {SERVICES.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className={`kyv-card p-7 ${s.big ? "lg:col-span-3" : "lg:col-span-2"}`}
                data-testid={`service-card-${i}`}
              >
                <div className="p-3 rounded-full bg-blue-500/10 text-blue-400 w-fit mb-5">
                  <s.icon size={22} />
                </div>
                <h3 className="font-display lowercase font-bold text-2xl tracking-tight mb-2">
                  {s.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* KAI — LIVE AI CHAT (home-page section, id=#kai for the FAB) */}
      <section
        id="kai"
        className="py-24 lg:py-32 bg-section-glow relative overflow-hidden"
        data-testid="kai-home-section"
      >
        <div className="absolute inset-0 grid-lines pointer-events-none opacity-60" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="font-display lowercase font-bold text-4xl sm:text-5xl tracking-tighter">
              meet <span className="text-blue-500">kai.</span>
            </h2>
            <p className="mt-3 text-xs sm:text-sm uppercase tracking-[0.25em] text-blue-400">
              ai consultant · kyvixe
            </p>
            <p className="mt-5 text-gray-400">
              ask anything about automations, chatbots, websites or bookings —
              kai answers instantly and can book you a discovery call when
              you're ready.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto"
          >
            <KaiChatPanel />
          </motion.div>
          <p className="mt-4 text-center text-xs text-gray-500">
            kai can make mistakes. for anything critical, book a call with a human.
          </p>
        </div>
      </section>

      {/* AI RECEPTIONIST */}
      <section className="py-24 lg:py-32 bg-section-glow" data-testid="ai-receptionist-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <h2 className="font-display lowercase font-bold text-4xl sm:text-5xl tracking-tighter">
              an ai receptionist <br />
              that works <span className="text-blue-500">24/7</span>
            </h2>
            <p className="mt-5 text-gray-400 leading-relaxed max-w-lg">
              trained on your services, prices and faq — she answers instantly,
              qualifies leads, and books meetings on whatsapp, instagram,
              facebook, and your website.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-3">
              {[
                { icon: MessageSquare, label: "instant replies" },
                { icon: Users, label: "lead qualification" },
                { icon: Calendar, label: "booking sync" },
                { icon: Send, label: "follow-ups" },
              ].map((f, i) => (
                <div key={i} className="kyv-card p-4 flex items-center gap-3">
                  <div className="p-2 rounded-full bg-blue-500/10 text-blue-400">
                    <f.icon size={16} />
                  </div>
                  <span className="text-sm text-gray-200 lowercase">{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Chat mockup — plays a scripted conversation when scrolled into view */}
          <AiChatDemo />
        </div>
      </section>

      {/* WEBSITES THAT DO MORE */}
      <section className="py-24 lg:py-32" data-testid="websites-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="absolute -inset-6 bg-blue-500/10 blur-3xl rounded-3xl" />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6 }}
              className="relative kyv-card p-3 sm:p-4"
              data-testid="websites-mockup"
            >
              {/* browser chrome */}
              <div className="flex items-center gap-2 px-3 pb-3 border-b border-white/10">
                <span className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
                <div className="flex-1 mx-3 h-6 bg-white/5 rounded-full flex items-center justify-center text-[10px] text-gray-400">
                  https://kyvixe.com
                </div>
                <div className="hidden sm:flex items-center gap-1 text-gray-500">
                  <span className="h-1 w-4 bg-white/10 rounded-full" />
                  <span className="h-1 w-4 bg-white/10 rounded-full" />
                  <span className="h-1 w-4 bg-white/10 rounded-full" />
                </div>
              </div>

              {/* fake site body */}
              <div className="relative overflow-hidden rounded-xl mt-3 bg-gradient-to-br from-[#0e0e17] via-[#0a0a0f] to-[#0e0e17] p-5 sm:p-6 min-h-[320px]">
                <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-blue-500/20 blur-3xl" />
                <div className="relative flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded-full bg-blue-500/30 border border-blue-400/40" />
                    <span className="text-xs font-display lowercase font-bold tracking-tighter">
                      studio<span className="text-blue-400">.</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-gray-500">
                    <span>work</span>
                    <span>about</span>
                    <span className="px-2 py-1 rounded-full bg-blue-600 text-white">book</span>
                  </div>
                </div>

                <div className="relative">
                  <p className="text-[10px] uppercase tracking-widest text-blue-400 mb-2">
                    live preview
                  </p>
                  <h4 className="font-display lowercase font-bold text-2xl sm:text-3xl tracking-tighter leading-tight">
                    grow faster. <br />
                    <span className="text-blue-500">automatically.</span>
                  </h4>
                  <p className="text-[11px] text-gray-400 mt-2 max-w-[220px]">
                    a modern site that books, chats, and converts while you sleep.
                  </p>

                  <div className="mt-4 grid grid-cols-3 gap-2">
                    {[
                      { k: "response", v: "3s" },
                      { k: "bookings", v: "+34%" },
                      { k: "uptime", v: "24/7" },
                    ].map((s, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 8 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 + i * 0.1, duration: 0.4 }}
                        className="rounded-lg border border-white/10 bg-white/[0.03] px-2 py-2"
                      >
                        <p className="text-[9px] uppercase tracking-widest text-gray-500">
                          {s.k}
                        </p>
                        <p className="font-display font-bold text-blue-400 text-lg leading-none mt-1">
                          {s.v}
                        </p>
                      </motion.div>
                    ))}
                  </div>

                  {/* floating booking widget */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    className="absolute right-0 sm:-right-2 top-1 kyv-card p-3 w-[150px] shadow-[0_10px_30px_-10px_rgba(59,130,246,0.5)] backdrop-blur-xl"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                      <p className="text-[9px] uppercase tracking-widest text-gray-400">
                        next slot
                      </p>
                    </div>
                    <p className="text-[11px] text-white font-medium">tomorrow</p>
                    <p className="font-display font-bold text-blue-400 text-lg leading-tight">
                      10:30 am
                    </p>
                    <button className="mt-2 w-full text-[10px] py-1.5 rounded-full bg-blue-600 text-white">
                      book now
                    </button>
                  </motion.div>
                </div>
              </div>

              {/* status strip */}
              <div className="flex items-center justify-between text-[10px] text-gray-500 px-3 pt-3">
                <div className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  <span>lighthouse 99 · seo 100 · a11y 98</span>
                </div>
                <span>built by kyvixe</span>
              </div>
            </motion.div>
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="font-display lowercase font-bold text-4xl sm:text-5xl tracking-tighter">
              websites that <span className="text-blue-500">do more</span>
            </h2>
            <p className="mt-5 text-gray-400 leading-relaxed max-w-lg">
              a modern website is a 24/7 salesperson. we ship yours with speed,
              seo, ai and booking baked in — so it earns its keep from day one.
            </p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {WEB_FEATURES.map((f, i) => (
                <li key={i} className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-blue-500/10 text-blue-400">
                    <f.icon size={14} />
                  </div>
                  <span className="text-sm text-gray-200 lowercase">{f.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24" data-testid="final-cta-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="relative kyv-card p-10 md:p-16 text-center overflow-hidden">
            <div className="absolute inset-0 bg-hero-glow opacity-70" />
            <div className="relative">
              <h2 className="font-display lowercase font-bold text-4xl sm:text-6xl tracking-tighter">
                automate. engage. <span className="text-blue-500">grow.</span>
              </h2>
              <p className="mt-5 text-gray-300 text-lg">
                don't miss leads. close more deals.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <Link to="/contact#book" className="kyv-btn-primary" data-testid="cta-book-btn">
                  book a call
                </Link>
                <Link to="/services" className="kyv-btn-ghost" data-testid="cta-services-btn">
                  see all services
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
